import { DIAG_CODES, type DiagCategory } from "./types";

/** Maps any thrown value / HTTP status into one of our safe codes. */
export function classifyHttpStatus(status: number): string {
  if (status === 401) return DIAG_CODES.unauthorized;
  if (status === 403) return DIAG_CODES.forbidden;
  if (status === 404) return DIAG_CODES.not_found;
  if (status === 408 || status === 504) return DIAG_CODES.timeout;
  if (status === 422 || status === 400) return DIAG_CODES.validation_failed;
  if (status === 429) return DIAG_CODES.rate_limited;
  if (status >= 500) return DIAG_CODES.server_error;
  return DIAG_CODES.unknown;
}

export function classifyNetworkError(err: unknown): string {
  const msg = String((err as any)?.message ?? err ?? "").toLowerCase();
  const name = String((err as any)?.name ?? "").toLowerCase();

  if (typeof navigator !== "undefined" && navigator.onLine === false) return DIAG_CODES.offline;
  if (name === "aborterror" || msg.includes("timeout") || msg.includes("timed out")) return DIAG_CODES.timeout;
  if (msg.includes("cors") || msg.includes("preflight") || msg.includes("access-control")) return DIAG_CODES.cors_blocked;
  if (msg.includes("err_name_not_resolved") || msg.includes("dns") || msg.includes("getaddrinfo")) return DIAG_CODES.dns_failure;
  if (msg.includes("failed to fetch") || msg.includes("networkerror") || msg.includes("network request failed") || msg.includes("load failed")) {
    return DIAG_CODES.network_failure;
  }
  if (msg.includes("unexpected token") || msg.includes("json")) return DIAG_CODES.malformed_response;
  return DIAG_CODES.unknown;
}

/** Postgres / PostgREST errors from supabase-js. */
export function classifySupabaseError(err: unknown): { category: DiagCategory; code: string } {
  const e = err as { code?: string; message?: string; status?: number } | null;
  const pg = String(e?.code ?? "");
  const msg = String(e?.message ?? "").toLowerCase();

  if (pg === "57014" || msg.includes("statement timeout") || msg.includes("canceling statement")) {
    return { category: "database", code: DIAG_CODES.database_timeout };
  }
  if (pg === "55P03" || msg.includes("lock")) return { category: "wallet", code: DIAG_CODES.wallet_lock_timeout };
  if (pg === "23505" || msg.includes("duplicate key")) return { category: "database", code: DIAG_CODES.duplicate_request };
  if (pg === "42501" || msg.includes("row-level security") || msg.includes("permission denied")) {
    return { category: "database", code: DIAG_CODES.forbidden };
  }
  if (pg === "PGRST301" || e?.status === 401) return { category: "auth", code: DIAG_CODES.unauthorized };
  if (e?.status === 404 || pg === "PGRST116") return { category: "database", code: DIAG_CODES.not_found };
  if (msg.includes("insufficient")) return { category: "wallet", code: DIAG_CODES.insufficient_balance };
  if (msg.includes("closed")) return { category: "bet", code: DIAG_CODES.market_closed };
  if (msg.includes("failed to fetch")) return { category: "network", code: DIAG_CODES.network_failure };
  return { category: "database", code: DIAG_CODES.server_error };
}
